<template>
  <v-container fluid>
    <v-form ref="formRef" validate-on="eager">
      <v-row>
        <template v-for="field in groupFields">
          <v-col
            v-if="!field.hidden && (!isReadOnlyDevice || fieldHasValue(field))"
            :key="field.uiKey"
            v-bind="getColProps(field)"
            align-self="end">
            <v-col cols="12" class="pa-0">
              <InputMetadata
                v-if="field.metadataKey && props.data[field.metadataKey]"
                :metadata="props.data[field.metadataKey]"
                :label="field.label"
                @highlight-bounding-box="onHighlightBoundingBox" />
            </v-col>
            <v-col cols="12" class="pa-0">
              <component
                :is="field.component"
                v-tooltip:top="getTooltipProps(field)"
                v-bind="{ disabled: readOnly, ...field.vBind }"
                :label="field.label"
                :rules="field.rules"
                :model-value="getFieldValue(field)"
                validate-on="eager"
                v-on="field.vOn"
                @update:model-value="(value, ...context) => updateFieldValue(field.key, value, ...context)" />
            </v-col>
            <v-col
              v-if="
                field.promptWithValue &&
                field.promptWithValue.toLowerCase() !== (getFieldValue(field) || '').toLowerCase()
              "
              cols="12"
              class="value-prompt-text py-1">
              AI Found:
              <v-btn
                variant="plain"
                size="small"
                color="primary"
                class="px-0"
                @click="updateFieldValue(field.key, field.promptWithValue)">
                {{ field.promptWithValue }}
              </v-btn>
            </v-col>
            <v-col v-if="warnings[field.key]" cols="12">
              <v-alert density="compact" variant="tonal" color="warning">
                {{ warnings[field.key] }}
              </v-alert>
            </v-col>
          </v-col>
        </template>
      </v-row>
    </v-form>
  </v-container>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { UIFieldList } from "../models/fields";
import InputMetadata from "./intakeReview/InputMetadata.vue";
import { useFieldTooltip } from "@/composables/useFieldTooltip";
import { useResponsive } from "@/composables/useResponsive";

const { isReadOnlyDevice } = useResponsive();

const props = defineProps({
  fields: {
    type: UIFieldList,
    required: true,
  },
  group: {
    type: String,
    required: false,
    default: null,
  },
  data: {
    type: Object,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const groupFields = computed(() => (props.group ? props.fields.forGroup(props.group) : props.fields.fields));
const emit = defineEmits(["attributeEdit", "deleteContent", "clearContent", "highlightBoundingBox"]);

const formRef = ref(null);
const warnings = ref({});

const { getTooltipDisplayValue } = useFieldTooltip();

defineExpose({
  formRef,
});

const testWarnings = (field, value) => {
  if (field.warnings.length > 0) {
    warnings.value[field.key] = null;
    const fieldWarnings = field.warnings.map((warning) => warning(value)).filter((warning) => warning !== null);
    if (fieldWarnings.length > 0) {
      // Just show the first warning
      warnings.value[field.key] = fieldWarnings[0];
    }
  }
};

// Add a rule to test warnings
// This is a hack that will allow us to sync errors and warnings
const testWarningsRule = (field, value) => {
  testWarnings(field, value);
  return true;
};

props.fields.fields.forEach((field) => {
  if (field.warnings.length > 0) {
    field.rules = [(v) => testWarningsRule(field, v), ...field.rules];
  }
});

onMounted(() => {
  props.fields.fields.forEach((field) => {
    const fieldValue = field.isExtraData ? props.data.extraData?.[field.key] : props.data[field.key];
    if (field.onMounted) {
      field.onMounted(fieldValue);
    }
    // Set default values if required
    if ((fieldValue === null || fieldValue === undefined) && field.defaultValue !== fieldValue) {
      updateFieldValue(field.key, field.defaultValue);
    }
    testWarnings(field);
  });
  formRef.value.validate();
});

const updateFieldValue = (fieldKey, value, ...context) => {
  const field = props.fields.getField(fieldKey);
  if (field.onAttributeChange) {
    value = field.onAttributeChange(value, ...context);
  }
  testWarnings(field, value);
  emit("attributeEdit", value, field, ...context);
};

const onHighlightBoundingBox = (metadata) => {
  emit("highlightBoundingBox", metadata);
};

const getFieldValue = (field) => {
  // If field provides a custom getValue function, use it (for fields that read from multiple keys)
  if (typeof field.getValue === "function") {
    return field.getValue();
  }
  return field.isExtraData ? props.data.extraData?.[field.key] : props.data[field.key];
};

// On read-only devices these forms render as a data sheet; suppress value-less
// fields so it reads clean, not as a list of blank labels.
const fieldHasValue = (field) => {
  const v = getFieldValue(field);
  if (typeof v === "boolean" || typeof v === "number") return true;
  if (Array.isArray(v)) return v.length > 0;
  return v !== null && v !== undefined && String(v).trim() !== "";
};

const getTooltipProps = (field) => {
  const value = getFieldValue(field);
  return getTooltipDisplayValue(value);
};

// Build responsive v-col props based on field.colSpan
// Accepts number (default cols) or object with breakpoint keys
const getColProps = (field) => {
  const span = field.colSpan;
  if (span && typeof span === "object") {
    const props = {};
    const allowed = ["cols", "sm", "md", "lg", "xl"];
    allowed.forEach((bp) => {
      if (typeof span[bp] === "number") {
        props[bp] = span[bp];
      }
    });
    if (props.cols === undefined) {
      props.cols = 12;
    }
    return props;
  }
  if (typeof span === "number") {
    return { cols: span };
  }
  return { cols: 12 };
};
</script>

<style scoped lang="scss">
.value-prompt-text {
  font-size: 0.8rem;
  color: $lh_color_blue_gray;
}
</style>
